import type { Theme } from "@emotion/react";
import type { StyledComponent } from "@emotion/styled";
import type { FunctionComponent, ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as AdSlotProps } from "~/components/advertisement/ad-slots/ad-slot";
import { normalizeJsxAttribute } from "~/lib/utils";
import type {
  contentSchemaRenderContent$data,
  contentSchemaRenderContent$key,
} from "~/queries/__generated__/contentSchemaRenderContent.graphql";

import { Blockquote } from "./blockquote";
import { BlockquoteQuote } from "./blockquote-quote";
import { Br } from "./br";
import { Div } from "./div";
import { SupportedContentNode } from "./enums";
import { Heading } from "./heading";
import { Iframe } from "./iframe";
import { Image } from "./image";
import { InlineWidget } from "./inline-widget";
import { Link } from "./link";
import { Paragraph } from "./paragraph";
import {
  StyledCaption,
  StyledEm,
  StyledLi,
  StyledOl,
  StyledS,
  StyledSpan,
  StyledStrong,
  StyledSub,
  StyledSup,
  StyledTbody,
  StyledTd,
  StyledTh,
  StyledThead,
  StyledTr,
  StyledUl,
  StyledUnderline,
} from "./styles";
import { Table } from "./table";
import { Text } from "./text";

export type ContentSchemaRenderProps = {
  children?: ReactNode;
  index: number;
  parentSchemaNode?: SchemaNode;
  reference: contentSchemaRenderContent$data;
  schemaNode: SchemaNode;
  theme?: Theme;
};

export type RenderComponent<
  Props extends ContentSchemaRenderProps = ContentSchemaRenderProps,
  Component = StyledComponent<Props>,
> = Component & {
  // IsHandlingOwnChildren indicate the component is responsible for rendering it's own children
  isHandlingOwnChildren?: boolean;
};

export type RenderMap<Component = RenderComponent> = Record<string, Component>;

export type Props = {
  advertising?: Advertising;
  extraRenderMap?: RenderMap<
    | RenderComponent
    | RenderComponent<ContentSchemaRenderProps, FunctionComponent<ContentSchemaRenderProps>>
    | null
  >;
  reference: contentSchemaRenderContent$key;
  schema: Schema | null | undefined;
};

type Advertising = {
  targeting?: AdSlotProps["targeting"];
  zone?: AdSlotProps["zone"];
};

export const RenderContext = createContext<{ advertising?: Advertising; schema: Schema }>({
  schema: [],
});
export const useRenderContext = () => useContext(RenderContext);

export const ContentSchemaRender: FunctionComponent<Props> = ({
  advertising,
  extraRenderMap = {},
  reference: reference_,
  schema,
  ...attributes
}) => {
  const content = useFragment(
    graphql`
      fragment contentSchemaRenderContent on Content {
        ...scmpYoutubeContent
        ...imageContent
        ...blockquoteQuoteContent
        ...inlineWidgetContent
        ...photoGalleryWidgetContent
        ...blockquoteQuoteGenericPostiesArticleContent
      }
    `,
    reference_,
  );

  const defaultRenderMap: RenderMap = {
    [SupportedContentNode.A]: Link,
    [SupportedContentNode.Blockquote]: Blockquote,
    [SupportedContentNode.BlockquoteQuote]: BlockquoteQuote,
    [SupportedContentNode.Br]: Br,
    [SupportedContentNode.Caption]: StyledCaption,
    [SupportedContentNode.Div]: Div,
    [SupportedContentNode.Em]: StyledEm,
    [SupportedContentNode.H1]: Heading,
    [SupportedContentNode.H2]: Heading,
    [SupportedContentNode.H3]: Heading,
    [SupportedContentNode.H4]: Heading,
    [SupportedContentNode.H5]: Heading,
    [SupportedContentNode.H6]: Heading,
    [SupportedContentNode.Iframe]: Iframe,
    [SupportedContentNode.InlineWidget]: InlineWidget,
    [SupportedContentNode.Image]: Image,
    [SupportedContentNode.Li]: StyledLi,
    [SupportedContentNode.Ol]: StyledOl,
    [SupportedContentNode.P]: Paragraph,
    [SupportedContentNode.S]: StyledS,
    [SupportedContentNode.Span]: StyledSpan,
    [SupportedContentNode.Strong]: StyledStrong,
    [SupportedContentNode.Sub]: StyledSub,
    [SupportedContentNode.Sup]: StyledSup,
    [SupportedContentNode.Table]: Table,
    [SupportedContentNode.Tbody]: StyledTbody,
    [SupportedContentNode.Td]: StyledTd,
    [SupportedContentNode.Th]: StyledTh,
    [SupportedContentNode.Thead]: StyledThead,
    [SupportedContentNode.Tr]: StyledTr,
    [SupportedContentNode.Ul]: StyledUl,
    [SupportedContentNode.U]: StyledUnderline,
    [SupportedContentNode.Text]: Text,
  };

  const renderMap = { ...defaultRenderMap, ...extraRenderMap };
  const deepRender = (
    index: number,
    schemaNode: SchemaNode,
    parentSchemaNode?: SchemaNode,
    itemIndex?: number,
  ): JSX.Element | null => {
    const Component = renderMap[schemaNode.type];
    if (!Component) {
      return null;
    }
    const renderChildren = () =>
      schemaNode.children?.reduce((childrenComponent, child, index_) => {
        const childComp = deepRender(index, child, schemaNode, index_);
        childComp && childrenComponent.push(childComp);
        return childrenComponent;
      }, [] as JSX.Element[]);

    return (
      <Component
        index={index}
        key={itemIndex}
        parentSchemaNode={parentSchemaNode}
        reference={content}
        schemaNode={schemaNode}
        {...normalizeJsxAttribute({ ...schemaNode.attribs, ...attributes })}
      >
        {!Component.isHandlingOwnChildren && renderChildren()}
      </Component>
    );
  };

  const contextValue = useMemo(
    () => ({
      advertising,
      schema: schema ?? [],
    }),
    [advertising, schema],
  );

  if (!schema) return null;
  return (
    <RenderContext.Provider value={contextValue}>
      {schema.map((s, index) => deepRender(index, s, undefined, index))}
    </RenderContext.Provider>
  );
};

ContentSchemaRender.displayName = "ContentSchemaRender";
